/* eslint-disable no-param-reassign */
/* eslint-disable class-methods-use-this */

class CartMilestones {
  constructor(cartDrawerInstance) {
    this.cartDrawer = cartDrawerInstance;
    this.settings = window.theme?.cart?.milestones || {};
    this.translations = window.theme?.translations?.cart_milestones || {};
    this.shippingThresholds = window.theme?.cart?.shippingThresholds || [];

    if (!this.settings.enabled) {
      return;
    }

    this.tiers = this.settings.tiers || [];
    if (this.tiers.length === 0) {
      return;
    }

    // Update free shipping milestone amounts from metaobject
    this.updateFreeShippingAmounts();

    // Sort tiers by amount in ascending order to ensure correct milestone logic
    // This handles cases where tiers might be defined in any order
    // Amounts are already updated in updateFreeShippingAmounts()
    this.tiers = [...this.tiers].sort((a, b) => (a.amount || 0) - (b.amount || 0));

    this.currentTier = 0;
    this.previousTier = 0;
    this.celebratedMilestones = [];
    this.appliedRewards = [];
    this.appliedDiscountCode = null;
    this.confetti = null;
    this.lastCartTotal = 0;
    this.currentGiftVariantId = null;
    this.currentGiftLineItemKey = null;
    this.isAddingGift = false; // Flag to prevent concurrent additions

    this.tracker = null;
    this.progressFill = null;
    this.progressMessage = null;
    this.progressBar = null;
    this.drawerRewards = null;
    this.giftSelector = null;
    this.celebrationOverlay = null;

    this.init();
  }

  init() {
    this.loadFromSession();
    this.cacheDOMElements();
    this.attachEventListeners();
  }

  getCountryCode() {
    // Try multiple sources for country code
    try {
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith('localization='))
        ?.split('=')[1];
      if (cookieValue) return cookieValue;
    } catch (e) {
      // Silent fail
    }

    const hiddenInput = document.querySelector('input[name="country_code"]');
    if (hiddenInput?.value) return hiddenInput.value;

    if (window.Shopify?.country) return window.Shopify.country;
    if (window.theme?.countryCode) return window.theme.countryCode;

    return 'DE'; // Default fallback
  }

  getShippingThresholdForCountry(countryCode = null) {
    if (!countryCode) {
      countryCode = this.getCountryCode();
    }

    // Find threshold for the country
    const thresholdEntry = this.shippingThresholds.find(
      (entry) => entry.countryCode === countryCode
    );

    if (!thresholdEntry || !thresholdEntry.threshold) {
      return null;
    }

    let thresholdValue = thresholdEntry.threshold;
    thresholdValue = parseFloat(thresholdValue);

    if (isNaN(thresholdValue)) {
      return null;
    }

    return thresholdValue;
  }

  updateFreeShippingAmounts() {
    const countryCode = this.getCountryCode();
    const shippingThreshold = this.getShippingThresholdForCountry(countryCode);

    // Store original schema amounts as fallback if not already stored
    this.tiers.forEach((milestone) => {
      if (milestone.type === 'free_shipping' && !milestone.originalAmount) {
        milestone.originalAmount = milestone.amount;
      }
    });

    if (shippingThreshold === null) {
      // If no metaobject threshold found, keep the schema amount (originalAmount or amount)
      // Restore original amount if it was previously overwritten
      this.tiers.forEach((milestone) => {
        if (milestone.type === 'free_shipping' && milestone.originalAmount !== undefined) {
          milestone.amount = milestone.originalAmount;
        }
      });
      return;
    }

    // Update all free shipping milestones to use the metaobject threshold
    this.tiers.forEach((milestone) => {
      if (milestone.type === 'free_shipping') {
        milestone.amount = shippingThreshold;
      }
    });
  }

  getMilestoneAmount(milestone) {
    // For free shipping, use metaobject threshold; otherwise use milestone amount
    if (milestone.type === 'free_shipping') {
      const threshold = this.getShippingThresholdForCountry();
      // Fallback to original schema amount if threshold doesn't exist
      if (threshold === null) {
        return milestone.originalAmount !== undefined ? milestone.originalAmount : milestone.amount;
      }
      return threshold;
    }
    return milestone.amount;
  }

  cacheDOMElements() {
    this.tracker = document.querySelector('[data-milestone-tracker]');
    this.progressFill = document.querySelector('[data-progress-fill]');
    this.progressMessage = document.querySelector('[data-progress-message]');
    this.progressBar = document.querySelector('.milestone-tracker__progress-bar');
    this.drawerRewards = document.querySelector('.drawer-rewards');
    this.giftSelector = document.querySelector('[data-gift-selector]');
    this.celebrationOverlay = document.querySelector('[data-celebration-overlay]');
  }

  attachEventListeners() {
    if (this.cartDrawer) {
      this.fetchCartData();
    }

    document.addEventListener('keydown', (e) => {
      if (
        e.key === 'Escape' &&
        this.celebrationOverlay &&
        !this.celebrationOverlay.hidden
      ) {
        this.closeCelebration();
      }
    });
  }

  async fetchCartData() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (!response.ok) return;
      const cartData = await response.json();
      this.updateProgress(cartData);
    } catch (error) {
      // Silent fail
    }
  }

  updateProgress(cartData) {
    if (!cartData || !this.progressFill || !this.tracker) return;

    // Calculate cart total excluding gift items
    const cartTotal = this.calculateCartTotalExcludingGifts(cartData);
    const itemCount = cartData.item_count || 0;
    this.lastCartTotal = cartTotal;
    this.previousTier = this.currentTier;
    this.currentTier = this.getCurrentTier(cartTotal);

    if (itemCount === 0 && this.progressMessage) {
      this.progressMessage.style.display = 'none';
    } else if (itemCount > 0 && this.progressMessage) {
      this.progressMessage.style.display = '';
    }

    const nextMilestone = this.getNextMilestone(cartTotal);
    // Get the highest milestone (last in sorted array) for fallback when all milestones are reached
    const lastMilestone = this.tiers[this.tiers.length - 1];

    // Calculate progress based on next milestone, or use last milestone if all milestones are reached
    const milestoneForProgress = nextMilestone || lastMilestone;
    
    if (milestoneForProgress) {
      const progress = this.calculateProgress(cartTotal, this.getMilestoneAmount(milestoneForProgress));
      this.progressFill.style.width = `${progress}%`;
      this.progressFill.setAttribute('aria-valuenow', Math.round(progress));

      if (this.currentTier > 0) {
        this.progressFill.style.backgroundColor = '#799F8E';
      } else {
        this.progressFill.style.backgroundColor = '#000';
      }

      if (this.currentTier > 0 && nextMilestone) {
        const reachedMilestones = [];
        for (let i = 0; i < this.currentTier; i++) {
          reachedMilestones.push(this.tiers[i].label);
        }
        const reachedRewardsText = reachedMilestones.join(', ');
        const boldReachedRewardsText = `${reachedRewardsText}`;

        let reachedCountryCode = '';
        for (let i = 0; i < this.currentTier; i++) {
          const milestone = this.tiers[i];
          if (milestone && milestone.type === 'free_shipping') {
            reachedCountryCode = this.getCountryCodeForMilestone(milestone);
            break;
          }
        }

        const nextCountryCode = this.getCountryCodeForMilestone(nextMilestone);

        const remaining = this.formatMoney(this.getMilestoneAmount(nextMilestone) - cartTotal);
        let message = this.translations.milestone_reached
          ?.replace('{{reward}}', boldReachedRewardsText)
          ?.replace('{{amount}}', remaining)
          ?.replace('{{next_reward}}', nextMilestone.label);

        if (message) {
          message = message.replace('{{country_code}}', '{{reached_country_code}}');
          message = message.replace('{{country_code}}', '{{next_country_code}}');
          message = message.replace('{{reached_country_code}}', reachedCountryCode);
          message = message.replace('{{next_country_code}}', nextCountryCode);
        }

        if (message) {
          message = message.replace(/^Geschafft!/, '<span class="milestone-tracker__highlighted-text">Geschafft!</span>');
          const escapedLabel = nextMilestone.label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          // Match "bis zu Ihrem [label]" or "bis zu [label]" to handle both cases
          const nextRewardRegex = new RegExp(
            `bis zu (?:Ihrem )?${escapedLabel}`,
            'g'
          );
          message = message.replace(nextRewardRegex, (match) => {
            // Check if "Ihrem" is in the match
            if (match.includes('Ihrem')) {
              return `bis zu Ihrem <span class="milestone-tracker__highlighted-text">${nextMilestone.label}</span>`;
            }
            return `bis zu <span class="milestone-tracker__highlighted-text">${nextMilestone.label}</span>`;
          });
        }

        if (this.progressMessage && message) {
          this.progressMessage.innerHTML = message;
        }
      } else if (this.currentTier > 0 && !nextMilestone) {
        const reachedMilestones = [];
        for (let i = 0; i < this.currentTier; i++) {
          reachedMilestones.push(this.tiers[i].label);
        }
        const reachedRewardsText = reachedMilestones.join(', ');
        const boldRewardsText = `<span class="milestone-tracker__highlighted-text">${reachedRewardsText}</span>`;

        let countryCode = '';
        for (let i = 0; i < this.currentTier; i++) {
          const milestone = this.tiers[i];
          if (milestone && milestone.type === 'free_shipping') {
            countryCode = this.getCountryCodeForMilestone(milestone);
            break;
          }
        }

        let message = this.translations.milestone_reached_no_next
          ?.replace('{{reward}}', boldRewardsText)
          ?.replace('{{country_code}}', countryCode);

        if (message) {
          const achievedText = this.translations.achieved || 'Geschafft!';
          message = message.replace(new RegExp(`^${achievedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i'), `<span class="milestone-tracker__highlighted-text">${achievedText}</span>`);
        }

        if (this.progressMessage && message) {
          this.progressMessage.innerHTML = message;
        }
      } else if (nextMilestone) {
        const remaining = this.formatMoney(this.getMilestoneAmount(nextMilestone) - cartTotal);
        const countryCode = this.getCountryCodeForMilestone(nextMilestone);
        let message = this.translations.title
          ?.replace('{{amount}}', `{{amount_placeholder}}`)
          ?.replace('{{reward}}', `{{reward_placeholder}}`)
          ?.replace('{{country_code}}', countryCode);

        if (message) {
          message = message.replace('{{amount_placeholder}}', `<span class="milestone-tracker__highlighted-text">${remaining}</span>`);
          message = message.replace('{{reward_placeholder}}', `<span class="milestone-tracker__highlighted-text">${nextMilestone.label}</span>`);
        }

        if (this.progressMessage && message) {
          this.progressMessage.innerHTML = message;
        }
      } else if (this.progressMessage) {
        this.progressMessage.innerHTML = this.translations.completed;
      }
    }

    // All milestones are reached if we've reached all tiers and there's no next milestone
    const allMilestonesReached = !nextMilestone && this.currentTier >= this.tiers.length;

    let hasGiftMilestone = false;
    for (let i = 0; i < this.currentTier; i++) {
      const milestone = this.tiers[i];
      if (milestone && milestone.type === 'gift' && milestone.giftProduct) {
        hasGiftMilestone = true;
        break;
      }
    }

    const finalMilestone = this.tiers[this.tiers.length - 1];
    const lastMilestoneIsGift = finalMilestone && finalMilestone.type === 'gift' && finalMilestone.giftProduct;
    const shouldHideTracker = allMilestonesReached && lastMilestoneIsGift;

    if (allMilestonesReached) {
      if (this.drawerRewards) {
        if (hasGiftMilestone) {
          this.drawerRewards.style.backgroundColor = '#E2EBE7';
        } else {
          this.drawerRewards.style.backgroundColor = '';
        }
      }
      if (this.tracker) {
        if (shouldHideTracker) {
          this.tracker.style.display = 'none';
        } else {
          this.tracker.style.display = '';
        }
      }
      if (this.progressFill) {
        this.progressFill.style.backgroundColor = '#799F8E';
      }
    } else {
      if (this.drawerRewards) {
        if (hasGiftMilestone) {
          this.drawerRewards.style.backgroundColor = '#E2EBE7';
        } else {
          this.drawerRewards.style.backgroundColor = '';
        }
      }
      if (this.tracker) {
        this.tracker.style.display = '';
      }
    }

    this.applyReachedRewards(cartTotal);
    // Handle gift lifecycle - don't await to avoid blocking
    this.handleGiftLifecycle().catch(() => {
      // Silent fail
    });

    if (this.currentTier > this.previousTier) {
      const reachedMilestone = this.tiers[this.currentTier - 1];
      if (
        reachedMilestone &&
        !this.celebratedMilestones.includes(this.currentTier)
      ) {
        this.celebrateMilestone(reachedMilestone, this.currentTier);
      }
    }

    this.saveToSession();

    document.dispatchEvent(
      new CustomEvent('milestone:progress-updated', {
        detail: { cartTotal, currentTier: this.currentTier, nextMilestone },
      })
    );
  }

  applyReachedRewards(cartTotal) {
    let giftMilestoneReached = null;
    let highestGiftTier = 0;

    // Find the highest gift milestone (only one gift should be active)
    for (let i = 0; i < this.currentTier; i++) {
      const tierIndex = i + 1;
      const milestone = this.tiers[i];

      if (milestone && !this.appliedRewards.includes(tierIndex)) {
        this.applyReward(milestone, false);
        this.appliedRewards.push(tierIndex);
      }

      // Track the highest gift milestone (only one gift product should be used)
      if (milestone && milestone.type === 'gift' && milestone.giftProduct) {
        giftMilestoneReached = milestone;
        highestGiftTier = tierIndex;
      }
    }

    if (giftMilestoneReached) {
      this.showGiftSelector(giftMilestoneReached.giftProduct, giftMilestoneReached);
    } else {
      this.hideGiftSelector();
    }
  }

  async handleGiftLifecycle() {
    // Get fresh cart data to check current state
    const cartData = await this.getCartData();
    if (!cartData) return;
    
    // Calculate cart total excluding gifts for milestone checks
    const cartTotalExcludingGifts = this.calculateCartTotalExcludingGifts(cartData);
    
    // Find the highest gift milestone that should be active based on cart total (excluding gifts)
    let activeGiftMilestone = null;
    let activeGiftTier = 0;

    // Recalculate current tier based on cart total excluding gifts
    const currentTierExcludingGifts = this.getCurrentTier(cartTotalExcludingGifts);

    for (let i = 0; i < currentTierExcludingGifts; i++) {
      const milestone = this.tiers[i];
      if (milestone && milestone.type === 'gift' && milestone.giftProduct) {
        activeGiftMilestone = milestone;
        activeGiftTier = i + 1;
      }
    }

    // Check if we need to add or remove gift
    if (activeGiftMilestone && activeGiftMilestone.giftProduct) {
      // If we don't have the gift variant ID yet, fetch it first
      if (!this.currentGiftVariantId) {
        await this.fetchGiftProduct(activeGiftMilestone.giftProduct);
        // Wait a bit for the variant ID to be set and saved
        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      // Check if gift is already in cart
      const giftInCart = this.findGiftInCart(cartData);

      if (giftInCart) {
        // Gift is already in cart - update the line item key and ensure quantity is 1
        this.currentGiftLineItemKey = giftInCart.key;
        
        // Ensure quantity is exactly 1 (not more)
        if (giftInCart.quantity > 1) {
          await this.updateGiftQuantity(1);
        }
        
        this.saveToSession();
        // Hide the gift item from cart display
        this.hideGiftFromCartDisplay();
      } else if (!giftInCart && this.currentGiftVariantId && !this.isAddingGift) {
        // Gift milestone reached but gift not in cart - add it
        // Only add if we're not already in the process of adding
        await this.addGiftToCart(activeGiftMilestone);
      }
    } else {
      // No gift milestone active - remove gift if it exists
      if (this.currentGiftLineItemKey) {
        await this.removeGiftFromCart();
      }
    }
  }

  async getCartData() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  findGiftInCart(cartData) {
    if (!cartData || !cartData.items || !this.currentGiftVariantId) return null;
    
    return cartData.items.find((item) => {
      return item.variant_id === this.currentGiftVariantId ||
             item.variant_id.toString() === this.currentGiftVariantId.toString();
    }) || null;
  }

  isGiftItem(item) {
    // Check if item has the gift property
    if (item.properties) {
      for (const key in item.properties) {
        if (key === '_gift_item' && item.properties[key] === 'true') {
          return true;
        }
      }
    }
    // Also check by variant ID if we have it stored
    if (this.currentGiftVariantId && item.variant_id) {
      return item.variant_id === this.currentGiftVariantId ||
             item.variant_id.toString() === this.currentGiftVariantId.toString();
    }
    return false;
  }

  calculateCartTotalExcludingGifts(cartData) {
    if (!cartData || !cartData.items) return 0;

    // Sum all items except gift items
    let total = 0;
    cartData.items.forEach((item) => {
      if (!this.isGiftItem(item)) {
        // Add the line price (quantity * price)
        total += item.final_line_price || item.line_price || 0;
      }
    });

    return total;
  }

  async addGiftToCart(giftMilestone = null) {
    if (!this.currentGiftVariantId) {
      return;
    }

    // Prevent concurrent additions - check flag BEFORE any async operations
    if (this.isAddingGift) {
      return;
    }

    // Set flag immediately to prevent race conditions
    this.isAddingGift = true;

    // Double-check if gift is already in cart before adding
    const cartDataBefore = await this.getCartData();
    if (cartDataBefore) {
      const existingGift = this.findGiftInCart(cartDataBefore);
      if (existingGift) {
        this.currentGiftLineItemKey = existingGift.key;
        this.saveToSession();
        this.isAddingGift = false;
        return;
      }
    }

    try {
      // Shopify cart API requires properties as a hash/object
      const formData = {
        items: [
          {
            id: this.currentGiftVariantId,
            quantity: 1, // Always add exactly 1
            properties: {
              '_gift_item': 'true',
            },
          },
        ],
      };

      const response = await fetch(
        `${window.Shopify?.routes?.root || '/'}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        }
      );

      const responseJson = await response.json();

      if (!response.ok) {
        this.isAddingGift = false;
        return;
      }

      // Apply discount code if available for the gift milestone
      // Note: The gift should be priced at 1€ in Shopify, or use a discount code
      if (giftMilestone && giftMilestone.discountCode) {
        this.applyDiscountCode(giftMilestone.discountCode);
      }
      
      // Fetch cart data to get the line item key
      const cartData = await this.getCartData();
      const giftItem = this.findGiftInCart(cartData);
      if (giftItem) {
        this.currentGiftLineItemKey = giftItem.key;
        
        // Ensure quantity is exactly 1 (in case it was added multiple times)
        if (giftItem.quantity > 1) {
          await this.updateGiftQuantity(1);
        }
      }
      
      // Update cart drawer if it exists
      if (this.cartDrawer && typeof this.cartDrawer.updateCart === 'function') {
        await this.cartDrawer.updateCart();
      }
      
      this.saveToSession();
    } catch (error) {
      // Silent fail
    } finally {
      this.isAddingGift = false;
    }
  }

  async updateGiftQuantity(quantity) {
    if (!this.currentGiftLineItemKey) return;

    try {
      const updates = {};
      updates[this.currentGiftLineItemKey] = quantity;

      const response = await fetch(
        `${window.Shopify?.routes?.root || '/'}cart/update.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates }),
        }
      );

      if (response.ok) {
        // Update cart drawer if it exists
        if (this.cartDrawer && typeof this.cartDrawer.updateCart === 'function') {
          await this.cartDrawer.updateCart();
        }
      }
    } catch (error) {
      // Silent fail
    }
  }

  async removeGiftFromCart() {
    // First, try to get fresh cart data to find the gift by variant ID
    // This handles cases where line item keys have shifted (e.g., when first item is removed)
    const cartData = await this.getCartData();
    let giftLineItemKey = this.currentGiftLineItemKey;
    
    // If we have a variant ID, try to find the gift in the cart to get the current line item key
    if (this.currentGiftVariantId && cartData) {
      const giftInCart = this.findGiftInCart(cartData);
      if (giftInCart && giftInCart.key) {
        giftLineItemKey = giftInCart.key;
      } else if (!giftInCart) {
        // Gift is not in cart anymore - just clear our stored references
        this.currentGiftLineItemKey = null;
        this.currentGiftVariantId = null;
        this.saveToSession();
        return;
      }
    }
    
    // If we still don't have a valid line item key, we can't remove the gift
    if (!giftLineItemKey) {
      // Clear stored references if we can't find the gift
      this.currentGiftLineItemKey = null;
      this.currentGiftVariantId = null;
      this.saveToSession();
      return;
    }

    try {
      const updates = {};
      updates[giftLineItemKey] = 0;

      const response = await fetch(
        `${window.Shopify?.routes?.root || '/'}cart/update.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ updates }),
        }
      );

      if (response.ok) {
        this.currentGiftLineItemKey = null;
        this.currentGiftVariantId = null;
        // Update cart drawer if it exists
        if (this.cartDrawer && typeof this.cartDrawer.updateCart === 'function') {
          await this.cartDrawer.updateCart();
        }
        this.saveToSession();
      }
    } catch (error) {
      // Silent fail
    }
  }

  hideGiftFromCartDisplay() {
    if (!this.currentGiftLineItemKey) return;
    
    // Hide gift items from cart display
    const cartItems = document.querySelectorAll('.cart-product');
    cartItems.forEach((item) => {
      const lineItemKey = item.querySelector('[data-line-item]')?.getAttribute('data-line-item');
      
      if (lineItemKey === this.currentGiftLineItemKey) {
        item.style.display = 'none';
        item.setAttribute('data-gift-item', 'true');
      }
    });

    // Also check by variant ID if line item key doesn't match
    if (this.currentGiftVariantId) {
      cartItems.forEach((item) => {
        const productId = item.getAttribute('data-product-id');
        // We need to check the cart data to match variant IDs
        // For now, we'll rely on the line item key matching
      });
    }
  }

  celebrateMilestone(milestone, tierIndex) {
    this.celebratedMilestones.push(tierIndex);

    if (!this.appliedRewards.includes(tierIndex)) {
      this.appliedRewards.push(tierIndex);
    }

    this.saveToSession();
    this.applyReward(milestone, true, true);

    if (this.settings.showCelebrations) {
      this.showCelebration(milestone);
    }

    document.dispatchEvent(
      new CustomEvent('milestone:reached', {
        detail: { milestone, tier: tierIndex },
      })
    );
  }

  applyReward(milestone, showCelebration = true, handleGiftSelector = false) {
    switch (milestone.type) {
      case 'discount':
        if (milestone.discountCode) {
          this.applyDiscountCode(milestone.discountCode);
        }
        break;
      case 'gift':
        if (handleGiftSelector && milestone.giftProduct) {
          this.showGiftSelector(milestone.giftProduct, milestone);
        }
        break;
      case 'free_shipping':
        break;
      default:
        break;
    }
  }

  applyDiscountCode(code) {
    this.appliedDiscountCode = code;
    this.saveToSession();

    const checkoutForms = document.querySelectorAll('[action="/checkout"]');
    checkoutForms.forEach((form) => {
      form.action = `/checkout?discount=${encodeURIComponent(code)}`;
    });

    const checkoutButtons = document.querySelectorAll('a[href*="/checkout"]');
    checkoutButtons.forEach((button) => {
      const currentHref = button.getAttribute('href') || '/checkout';
      const baseUrl = currentHref.split('?')[0];
      button.setAttribute(
        'href',
        `${baseUrl}?discount=${encodeURIComponent(code)}`
      );
    });

    document.dispatchEvent(
      new CustomEvent('milestone:discount-applied', {
        detail: { code },
      })
    );
  }

  async showGiftSelector(productHandle, giftMilestone = null) {
    if (!this.giftSelector) return;

    const subtitleEl = this.giftSelector.querySelector('.gift-selector__subtitle');
    if (subtitleEl) {
      const nonGiftRewards = [];
      const giftReward = giftMilestone ? giftMilestone.label : '';

      for (let i = 0; i < this.currentTier; i++) {
        const milestone = this.tiers[i];
        if (milestone && milestone.type !== 'gift') {
          nonGiftRewards.push(milestone.label);
        }
      }

      const nonGiftRewardsText = nonGiftRewards.length > 0
        ? nonGiftRewards.join(', ')
        : '';

      let countryCode = '';
      for (let i = 0; i < this.currentTier; i++) {
        const milestone = this.tiers[i];
        if (milestone && milestone.type === 'free_shipping') {
          countryCode = this.getCountryCodeForMilestone(milestone);
          break;
        }
      }

      // Find the index of the gift milestone in tiers array
      let giftMilestoneIndex = -1;
      if (giftMilestone) {
        giftMilestoneIndex = this.tiers.findIndex(
          (tier) => tier === giftMilestone || 
          (tier.type === 'gift' && tier.giftProduct === giftMilestone.giftProduct)
        );
      }

      // Check if gift is the last milestone
      const giftIsLastMilestone = giftMilestoneIndex >= 0 && 
        giftMilestoneIndex === this.tiers.length - 1;

      // Use appropriate translation based on whether gift is last milestone
      let message;
      if (giftIsLastMilestone) {
        // Gift is the last milestone - show previous rewards
        message = this.translations.gift_message ||
          'Herzlichen Glückwunsch, Sie erhalten {{non_gift_rewards}} und Ihr {{gift_reward}}.';
        
        if (nonGiftRewardsText) {
          const boldNonGiftRewards = `<span class="milestone-tracker__highlighted-text">${nonGiftRewardsText}</span>`;
          message = message.replace('{{non_gift_rewards}}', boldNonGiftRewards);
        } else {
          message = message.replace('{{non_gift_rewards}} und ', '');
        }
      } else {
        // Gift is not the last milestone - don't show previous rewards
        message = this.translations.gift_message_no_previous ||
          'Herzlichen Glückwunsch, Sie erhalten Ihr {{gift_reward}}.';
      }

      const boldGiftReward = `<span class="milestone-tracker__highlighted-text">${giftReward}</span>`;
      message = message.replace('{{gift_reward}}', boldGiftReward);
      message = message.replace('{{country_code}}', countryCode);
      subtitleEl.innerHTML = message;
    }

    this.giftSelector.style.maxHeight = '500px';
    this.giftSelector.style.opacity = '1';
    this.giftSelector.style.padding = '27px 24px';
    this.giftSelector.style.borderTop = '1px solid #e5e7eb';
    this.giftSelector.style.backgroundColor = '#E2EBE7';
    this.giftSelector.setAttribute('aria-hidden', 'false');
    this.giftSelector.setAttribute('data-state', 'expanded');

    await this.fetchGiftProduct(productHandle);

    document.dispatchEvent(
      new CustomEvent('milestone:gift-available', {
        detail: { productHandle },
      })
    );
  }

  async fetchGiftProduct(productHandle) {
    const productsContainer = this.giftSelector?.querySelector('[data-gift-products]');
    if (!productsContainer) return;

    try {
      const response = await fetch(
        `${window.Shopify?.routes?.root || '/'}products/${productHandle}.js`
      );
      if (!response.ok) {
        const errorText = this.translations.gift_load_error || 'Fehler beim Laden des Geschenks.';
        productsContainer.innerHTML = `<p class="body-light text-dark-gray">${errorText}</p>`;
        return;
      }

      const product = await response.json();

      if (!product || !product.variants || product.variants.length === 0) {
        const noGiftsText = this.translations.gift_no_products || 'Keine Geschenke verfügbar.';
        productsContainer.innerHTML = `<p class="body-light text-dark-gray">${noGiftsText}</p>`;
        return;
      }

      // Find first available variant
      const variant = product.variants.find((v) => {
        // Check if variant is available (inventory_quantity > 0 or available: true)
        return v.available === true || 
               (v.inventory_quantity !== null && v.inventory_quantity > 0) ||
               (v.inventory_management === null); // If inventory management is null, it's typically available
      });

      if (!variant) {
        const unavailableText = this.translations.gift_unavailable || 'Geschenk ist nicht verfügbar.';
        productsContainer.innerHTML = `<p class="body-light text-dark-gray">${unavailableText}</p>`;
        return;
      }
      
      // Store the variant ID for auto-adding to cart BEFORE generating HTML
      if (variant && variant.id) {
        this.currentGiftVariantId = variant.id;
        this.saveToSession(); // Save immediately so it's available
      }

      // Get product image - Shopify product API can have different structures
      let image = 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-1_large.png';
      
      if (product.featured_image) {
        // Featured image is a direct URL string
        image = product.featured_image;
      } else if (product.images && product.images.length > 0) {
        // Images array - could be array of strings or objects
        const firstImage = product.images[0];
        if (typeof firstImage === 'string') {
          image = firstImage;
        } else if (firstImage.src) {
          image = firstImage.src;
        } else if (firstImage.url) {
          image = firstImage.url;
        }
      }
      const variantTitle = variant && variant.title && variant.title !== 'Default Title' ? variant.title : '';

      let price = '1,00'; // Default to 1€ as requested
      if (variant && variant.price) {
        const priceValue = typeof variant.price === 'string'
          ? parseFloat(variant.price)
          : variant.price;
        price = (priceValue / 100).toFixed(2).replace('.', ',');
      }

      const productUrl = product.handle
        ? `${window.Shopify?.routes?.root || '/'}products/${product.handle}`
        : '#';

      productsContainer.innerHTML = `
        <div class="gift-selector__product" data-product-id="${product.id}" data-variant-id="${variant ? variant.id : ''}">
          <a href="${productUrl}" class="gift-selector__product-link">
            <div class="gift-selector__product-image">
              <img src="${image}" alt="${product.title}" loading="lazy">
            </div>
            <div class="gift-selector__product-info">
              <p class="gift-selector__product-vendor">${product.vendor}</p>
              <h4 class="gift-selector__product-title">${product.title}</h4>
              ${variantTitle ? `<p class="gift-selector__product-variant">${variantTitle}</p>` : ''}
              <p class="gift-selector__product-price">${price}€</p>
            </div>
          </a>
        </div>
      `;
    } catch (error) {
      if (productsContainer) {
        const errorText = this.translations.gift_load_error || 'Fehler beim Laden des Geschenks.';
        productsContainer.innerHTML = `<p class="body-light text-dark-gray">${errorText}</p>`;
      }
    }
  }

  hideGiftSelector() {
    if (!this.giftSelector) return;

    this.giftSelector.style.maxHeight = '0';
    this.giftSelector.style.opacity = '0';
    this.giftSelector.style.padding = '0';
    this.giftSelector.style.borderTop = 'none';
    this.giftSelector.style.backgroundColor = 'transparent';
    this.giftSelector.setAttribute('aria-hidden', 'true');
    this.giftSelector.setAttribute('data-state', 'collapsed');
  }

  showCelebration(milestone) {
    if (!this.celebrationOverlay) return;

    const messageEl = this.celebrationOverlay.querySelector('[data-celebration-message]');
    const subtitleEl = this.celebrationOverlay.querySelector('[data-celebration-subtitle]');

    if (subtitleEl) {
      const message = this.translations.celebration_message?.replace(
        '{{reward}}',
        milestone.label
      );
      if (!message) {
        const unlockedText = this.translations.unlocked || 'freigeschaltet!';
        message = `${milestone.label} ${unlockedText}`;
      }
      subtitleEl.textContent = message;
    }

    this.celebrationOverlay.hidden = false;
    this.celebrationOverlay.classList.remove('opacity-0');
    this.celebrationOverlay.classList.add('opacity-100');

    if (messageEl) {
      setTimeout(() => {
        messageEl.classList.remove('scale-0');
        messageEl.classList.add('scale-100');
      }, 100);
    }

    if (typeof window.ConfettiAnimation !== 'undefined') {
      this.confetti = new window.ConfettiAnimation('confetti-canvas', {
        colors: ['#255a2e', '#80a758', '#d9e6a1', '#e83a43'],
      });
      this.confetti.init();
    }

    setTimeout(() => {
      this.closeCelebration();
    }, 3000);
  }

  closeCelebration() {
    if (!this.celebrationOverlay) return;

    const messageEl = this.celebrationOverlay.querySelector('[data-celebration-message]');

    if (messageEl) {
      messageEl.classList.remove('scale-100');
      messageEl.classList.add('scale-0');
    }

    this.celebrationOverlay.classList.remove('opacity-100');
    this.celebrationOverlay.classList.add('opacity-0');

    setTimeout(() => {
      this.celebrationOverlay.hidden = true;
    }, 300);

    if (this.confetti) {
      this.confetti.stop();
      this.confetti = null;
    }
  }

  calculateProgress(cartTotal, milestoneAmount) {
    if (milestoneAmount === 0) return 0;
    return Math.min((cartTotal / milestoneAmount) * 100, 100);
  }

  getCurrentTier(cartTotal) {
    // Count how many milestones have been reached
    // Tiers are sorted by amount, so we can iterate and count
    let tier = 0;
    this.tiers.forEach((milestone) => {
      if (cartTotal >= this.getMilestoneAmount(milestone)) {
        tier += 1;
      }
    });
    return tier;
  }

  getNextMilestone(cartTotal) {
    // Find the lowest unreached milestone
    // Since tiers are sorted by amount, the first unreached is the next one
    return this.tiers.find((m) => cartTotal < this.getMilestoneAmount(m)) || null;
  }

  formatMoney(cents) {
    const euros = cents / 100;
    return `${euros.toFixed(2).replace('.', ',')}€`;
  }

  getCountryCodeForMilestone(milestone) {
    if (!milestone || milestone.type !== 'free_shipping') {
      return '';
    }
    
    let countryCode = '';
    
    try {
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith('localization='))
        ?.split('=')[1];
      if (cookieValue) {
        countryCode = cookieValue;
      }
    } catch (e) {
      // Silent fail
    }
    
    if (!countryCode) {
      const hiddenInput = document.querySelector('input[name="country_code"]');
      if (hiddenInput?.value) {
        countryCode = hiddenInput.value;
      }
    }
    
    if (!countryCode && window.Shopify?.country) {
      countryCode = window.Shopify.country;
    }
    
    if (!countryCode && window.theme?.countryCode) {
      countryCode = window.theme.countryCode;
    }
    
    return countryCode ? ` (${countryCode})` : '';
  }

  saveToSession() {
    try {
      const state = {
        celebratedMilestones: this.celebratedMilestones,
        appliedRewards: this.appliedRewards,
        appliedDiscountCode: this.appliedDiscountCode,
        lastCartTotal: this.lastCartTotal || 0,
        currentGiftVariantId: this.currentGiftVariantId,
        currentGiftLineItemKey: this.currentGiftLineItemKey,
        lastUpdateTimestamp: Date.now(),
      };
      sessionStorage.setItem('cart_milestones_state', JSON.stringify(state));
    } catch (e) {
      // Silent fail
    }
  }

  loadFromSession() {
    try {
      const saved = sessionStorage.getItem('cart_milestones_state');
      if (saved) {
        const state = JSON.parse(saved);
        this.celebratedMilestones = state.celebratedMilestones || [];
        this.appliedRewards = state.appliedRewards || [];
        this.appliedDiscountCode = state.appliedDiscountCode || null;
        this.currentGiftVariantId = state.currentGiftVariantId || null;
        this.currentGiftLineItemKey = state.currentGiftLineItemKey || null;
        this.previousTier = this.getCurrentTier(state.lastCartTotal || 0);
      }
    } catch (e) {
      // Silent fail
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartMilestones);
} else {
  initCartMilestones();
}

function initCartMilestones() {
  const checkCartDrawer = setInterval(() => {
    const cartDrawer = document.querySelector('cart-drawer');
    const milestoneTracker = document.querySelector('[data-milestone-tracker]');

    if (cartDrawer && milestoneTracker) {
      clearInterval(checkCartDrawer);
      window.cartMilestones = new CartMilestones(cartDrawer);
    }
  }, 100);

  setTimeout(() => clearInterval(checkCartDrawer), 5000);
}
